/* eslint-disable no-unused-vars, no-console, camelcase */
import type { MediaPrivacy, VisitorUploads } from './../../wix-ricos-common';
import { getMediaFileType, MediaType } from './../../wix-ricos-common';
import type { RichContentEditorProps } from './../../ricos-common';
import type {
  AudioComponentData,
  FileComponentData,
  ImageComponentData,
  UpdateEntityFileUploadFunc,
  VideoComponentData,
} from './../../ricos-content';
import {
  AUDIO_TYPE,
  FILE_UPLOAD_TYPE,
  IMAGE_TYPE,
  MediaUploadErrorKey,
  POLL_TYPE,
  VIDEO_TYPE,
} from './../../ricos-content';
import type { GetWixClientPromise } from './../../_shared/request-service-provider/use-get-wix-client-promise';

import { getUploadError } from './mediaErrorHandler';
import { MediaManagerUploader } from './mediaManagerUploader';
import type {
  MediaManagerSDKError,
  RawAudioData,
  RawFileData,
  RawImageData,
  RawVideoData,
} from './uploadTypes';

export const getHighestQualityVideo = (
  videos: { format: string; quality: string; url: string }[]
) => {
  const mp4 = videos.filter(video => video.format === 'mp4');
  const getVideoQuality = (video: { quality: string }) => parseInt(video.quality, 10);
  return mp4.reduce((result, video) =>
    getVideoQuality(video) > getVideoQuality(result) ? video : result
  );
};

export const getFilePluginComponentData = (
  fileName: string,
  fileUrl: string,
  type: string,
  privacy: MediaPrivacy = 'public',
  size?: number
): FileComponentData => {
  const componentData = {
    name: fileName,
    type,
    id: fileUrl,
    privacy,
  };
  return size ? { ...componentData, size } : componentData;
};

const getFolderId = (userId: string, siteOwnerId: string, visitorUploads?: VisitorUploads) => {
  const isOwner = siteOwnerId === userId;
  const { folderId, shouldIncludeSiteOwner } = visitorUploads || {};
  const shouldUploadToFolder = shouldIncludeSiteOwner || !isOwner;
  return shouldUploadToFolder ? folderId : null;
};

function isFileSizeExcceeded(
  file: File,
  fileUploadSizeLimit?: number
): fileUploadSizeLimit is number {
  return !!fileUploadSizeLimit && file.size >= fileUploadSizeLimit * 1024 * 1024;
}

let mediaManagerUploader: MediaManagerUploader;

export default function uploadStrategy(
  metaSiteId: string,
  userId: string,
  siteOwnerId: string,
  instance: string,
  hasPlugin: (pluginType: string) => boolean,
  getConfig: (pluginType: string) => Record<string, any>,
  getWixClientPromise: GetWixClientPromise,
  visitorUploads?: VisitorUploads,
  consumer?: string,
  fileUploadSizeLimit?: number
): RichContentEditorProps {
  if (!(instance && metaSiteId)) {
    return {
      config: {},
    };
  }

  const folderId = getFolderId(userId, siteOwnerId, visitorUploads);

  if (!mediaManagerUploader) {
    mediaManagerUploader = new MediaManagerUploader(getWixClientPromise, consumer, folderId);
  }

  const handleUploadError = (
    error: MediaManagerSDKError,
    updateEntity: UpdateEntityFileUploadFunc<
      ImageComponentData & VideoComponentData & AudioComponentData & FileComponentData
    >
  ) => {
    const isOwner = siteOwnerId === userId;
    const mediaUploadError = getUploadError(error, metaSiteId, isOwner);
    updateEntity({ error: mediaUploadError });
  };

  const withFileSizeLimitation = (uploadFunction: any) => {
    return (
      file: File,
      updateEntity: UpdateEntityFileUploadFunc<
        ImageComponentData & VideoComponentData & AudioComponentData & FileComponentData
      >
    ) =>
      isFileSizeExcceeded(file, fileUploadSizeLimit)
        ? updateEntity({
            error: {
              key: MediaUploadErrorKey.SIZE_LIMIT,
              args: { maxLimit: fileUploadSizeLimit },
            },
          })
        : uploadFunction(file, updateEntity);
  };

  const imageUpload = (
    file: File,
    updateEntity: UpdateEntityFileUploadFunc<ImageComponentData>
  ) => {
    return mediaManagerUploader
      .upload(file, MediaType.IMAGE)
      .then(({ file_name, original_file_name, height, width }: RawImageData) => {
        updateEntity({
          data: {
            id: file_name,
            height,
            width,
            original_file_name,
            file_name,
          },
        });
      })
      .catch((error: MediaManagerSDKError) => {
        return handleUploadError(error, updateEntity);
      });
  };

  const videoUpload = (
    file: File,
    updateEntity: UpdateEntityFileUploadFunc<VideoComponentData>
  ) => {
    return mediaManagerUploader
      .upload(file, MediaType.VIDEO)
      .then(({ file_output: { video, image } }: RawVideoData) => {
        updateEntity({
          data: {
            pathname: getHighestQualityVideo(video).url,
            thumbnail: {
              pathname: image[0].url,
              width: image[0].width,
              height: image[0].height,
            },
            title: file.name,
          },
        });
      })
      .catch((error: MediaManagerSDKError) => handleUploadError(error, updateEntity));
  };

  const audioUpload = (
    file: File,
    updateEntity: UpdateEntityFileUploadFunc<AudioComponentData | ImageComponentData>
  ) => {
    const isCoverImage = file.type.includes('image');
    const mediaType = isCoverImage ? MediaType.IMAGE : MediaType.AUDIO;
    return mediaManagerUploader
      .upload(file, mediaType)
      .then((res: RawAudioData | RawImageData) => {
        if (isCoverImage) {
          const { file_name, original_file_name, height, width } = res as RawImageData;
          return updateEntity({
            data: {
              id: file_name,
              height,
              width,
              original_file_name,
              file_name,
            },
          });
        } else {
          const {
            file_output: { audio },
          } = res as RawAudioData;
          updateEntity({
            data: {
              audio: { src: { id: audio[0].url } },
            },
          });
        }
      })
      .catch((error: MediaManagerSDKError) => handleUploadError(error, updateEntity));
  };

  const getUnsupportedExtensionError = (fileName: string) => {
    return {
      key: MediaUploadErrorKey.UNSUPPORTED_EXTENSION,
      args: { extension: fileName.split('.').pop()?.toLowerCase() || '' },
    };
  };

  const galleryUpload = (
    file: File,
    updateEntity: UpdateEntityFileUploadFunc<ImageComponentData | VideoComponentData>
  ) => {
    const fileType = getMediaFileType(file.name);
    if (fileType === MediaType.IMAGE) {
      return imageUpload(file, updateEntity as UpdateEntityFileUploadFunc<ImageComponentData>);
    } else if (fileType === MediaType.VIDEO) {
      return videoUpload(file, updateEntity as UpdateEntityFileUploadFunc<VideoComponentData>);
    } else {
      return updateEntity({
        error: getUnsupportedExtensionError(file.name),
      });
    }
  };

  const fileUpload = (file: File, updateEntity: UpdateEntityFileUploadFunc<FileComponentData>) => {
    const fileType = getMediaFileType(file.name);
    const privacy: MediaPrivacy = getConfig(FILE_UPLOAD_TYPE).privacy || 'public';
    return mediaManagerUploader
      .upload(file, fileType, { privacy })
      .then(({ original_file_name: name, media_type, file_url, file_output }: RawFileData) => {
        const fileUrl = file_output?.video
          ? getHighestQualityVideo(file_output.video).url
          : file_url;
        const type = file.name.split('.').length > 1 ? file.name.split('.').pop() : media_type;
        const componentData = getFilePluginComponentData(name, fileUrl, type || '', privacy);
        updateEntity({ data: componentData });
      })
      .catch((error: MediaManagerSDKError) => handleUploadError(error, updateEntity));
  };

  const uploadFunctions = {
    [IMAGE_TYPE]: withFileSizeLimitation(galleryUpload),
    [VIDEO_TYPE]: withFileSizeLimitation(videoUpload),
    [AUDIO_TYPE]: withFileSizeLimitation(audioUpload),
    [FILE_UPLOAD_TYPE]: withFileSizeLimitation(fileUpload),
  };

  return {
    helpers: {
      ...((hasPlugin(IMAGE_TYPE) || hasPlugin(POLL_TYPE)) && {
        handleFileUpload: uploadFunctions[IMAGE_TYPE],
      }),
    },
    config: {
      ...(hasPlugin(VIDEO_TYPE) && {
        [VIDEO_TYPE]: {
          handleFileUpload: uploadFunctions[VIDEO_TYPE],
          enableCustomUploadOnMobile: true,
        },
      }),
      ...(hasPlugin(AUDIO_TYPE) && {
        [AUDIO_TYPE]: {
          handleFileUpload: uploadFunctions[AUDIO_TYPE],
        },
      }),
      ...(hasPlugin(FILE_UPLOAD_TYPE) && {
        [FILE_UPLOAD_TYPE]: {
          onFileSelected: uploadFunctions[FILE_UPLOAD_TYPE],
          accept: '*',
        },
      }),
    },
  };
}
